<script>
import Teleport from 'vue2-teleport';

import DefaultLayout from '@/layouts/Default.vue';
import CommonSection from '@/components/CommonSection.vue';
import ProjectFilesContainer from '@/components/project/ProjectFilesContainer.vue';
import ProjectFileCategoriesContainer from '@/components/project/ProjectFileCategoriesContainer.vue';
import ProjectFileFiltersContainer from '@/components/project/ProjectFileFiltersContainer.vue';
import ProjectFileUploader from '@/components/ProjectFileUploader.vue';
import BasePopup from '@/components/BasePopup.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import BaseInput from '@/components/BaseInput.vue';
import Pagination from '@/components/Pagination.vue';
import utils from '@/utils/common';
import CommonDropdown from '@/components/CommonDropdown.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import EmptyList from '@/components/ui/EmptyList.vue';
import projectFileApi from '@/api/projectFile';
import projectFileCategoryApi from '@/api/projectFileCategory';
import Access from '@/utils/access';

import ProjectFilesFooterBar from '../components/project/ProjectFilesFooterBar.vue';

export default {
  name: 'ProjectFilesPage',
  components: {
    DefaultLayout,
    CommonSection,
    ProjectFilesContainer,
    ProjectFileCategoriesContainer,
    ProjectFileFiltersContainer,
    ProjectFileUploader,
    BasePopup,
    BaseSelect,
    BaseInput,
    Pagination,
    CommonDropdown,
    BaseCheckbox,
    EmptyList,
    ProjectFilesFooterBar,
    Teleport,
  },
  data() {
    return {
      filters: {
        fileName: '',
        extensions: [],
        members: [],
        selected: null,
      },
      projectId: +this.$route.params.projectId,
      selectedFileCategory: null,
      staticSelection: false,
      pageSize: 12,
      page: 1,
      filesStateDataCopyMarked: null,
      device: 'desktop',
      categoryInputPopupOpened: false,
      categoryNewName: null,
      categoriesAction: null,
      categoriesActioned: [],
      categoryPopupSelected: null,
      categorySelectPopupOpened: false,
      filesAction: null,
      filesActioned: [],
      sorter: {
        value: null,
        direction: null,
      },
      isTablet: false,
      orientationType: 'square',
      isOpenedMenu: false,
      isOpenedPopup: false,
      confirmFilesPopupOpened: false,
      confirmCategoriesPopupOpened: false,
      dragActive: false,
      menuItems: [
        { text: 'Переместить', icon: 'arrows-crossed', handler: () => this.clickSelectedMenu('move') },
        { text: 'Скачать', icon: 'arrow-download', handler: () => this.clickSelectedMenu('download') },
        { text: 'Удалить', icon: 'archive', handler: () => this.clickSelectedMenu('delete') }],
    };
  },
  mounted() {
    document.addEventListener('click', this.closeMenu);
    window.addEventListener('resize', this.checkResize);
    window.addEventListener('dragend', () => {
      this.dragActive = false;
    });
    this.checkResize();
  },
  watch: {
    projectFilesStateData(data) {
      if (!data) this.filesStateDataCopyMarked = null;
      else {
        this.filesStateDataCopyMarked = JSON.parse(JSON.stringify(data)).map(file => {
          // eslint-disable-next-line no-param-reassign
          if (!('selected' in file)) file.selected = false;
          return file;
        });
      }
    },
    filters: {
      deep: true,
      handler() {
        this.page = 1;
      },
    },
    selectedFileCategory() {
      this.page = 1;
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
    document.removeEventListener('resize', this.checkResize);
  },
  computed: {
    project() { return this.$store.state.project.project; },
    currUser() { return this.$store.state.auth.user; },
    access() { return this.project && new Access(this.project, this.currUser); },
    categoriesAreEditable() {
      return this.access?.isProjectEditable() && (
        this.access?.isProjectAuthor()
        || this.access?.isProjectAdmin()
      );
    },
    projectFilesStateData() {
      return this.$store.state.projectFile.files[this.projectId] || [];
    },
    projectCategoriesStateData() {
      const stateData = this.$store.state.projectFile.categories[this.projectId] ?? [];
      const allCategory = {
        '@id': null,
        id: null,
        name: 'Все',
      };

      return [allCategory, ...stateData];
    },
    categoriesPopup() {
      return this.projectCategoriesStateData.filter(category => category.id).map(category => ({ label: category.name, value: category['@id'] }));
    },
    filteredSortedFilesData() {
      if (!this.filesStateDataCopyMarked) return [];
      return this.filesStateDataCopyMarked.filter(file => {
        if (this.filters.extensions.length && !this.filters.extensions.includes(file.file.extension)) return false;
        if (this.filters.members.length && !this.filters.members.includes(file.file.owner.id)) return false;
        if (this.filters.fileName && !file.file.name.toLowerCase().includes(this.filters.fileName.toLowerCase())) return false;
        return (!(this.filters.selected !== null && file.selected !== this.filters.selected));
      }).sort((file1, file2) => {
        if (!this.sorter.value) return 0;
        const filesComparing = this.sorter.direction === 'asc' ? [file2, file1] : [file1, file2];

        if (this.sorter.value === 'fileName') return filesComparing[1].file.name.localeCompare(filesComparing[0].file.name);
        if (this.sorter.value === 'userName') return filesComparing[1].file.owner.name.localeCompare(filesComparing[0].file.owner.name);
        if (this.sorter.value === 'createdAt') return new Date(filesComparing[1].file.createdAt) - new Date(filesComparing[0].file.createdAt);
        if (this.sorter.value === 'size') return filesComparing[1].file.size - filesComparing[0].file.size;
        return 0;
      });
    },
    selectedCategoryFilesData() {
      if (!this.filteredSortedFilesData) return [];
      return this.filteredSortedFilesData.filter(fileData => this.selectedFileCategory === null || fileData.category === this.selectedFileCategory);
    },
    selectedCategoryFilesDataNoFilter() {
      if (!this.filesStateDataCopyMarked) return [];
      return this.filesStateDataCopyMarked.filter(fileData => this.selectedFileCategory === null || fileData.category === this.selectedFileCategory);
    },
    selectedFilteredSortedFilesData() {
      return this.filteredSortedFilesData.filter(fileData => fileData.selected);
    },
    selectedFilesWord() {
      return utils.pluralWord(this.selectedFilteredSortedFilesData.length, 'файл', 'файла', 'файлов');
    },
    selectedFilesStorageSize() {
      const size = this.selectedFilteredSortedFilesData.reduce((acc, current) => acc + current.file.size, 0);
      return utils.storageSizeCounter(size);
    },
    pagedFilesData() {
      const startRange = (this.page - 1) * this.pageSize;
      const endRange = (this.page * this.pageSize < this.selectedCategoryFilesData.length) ? this.page * this.pageSize : this.selectedCategoryFilesData.length;
      return this.selectedCategoryFilesData.slice(startRange, endRange);
    },
    isAllFilesSelected() {
      return (!!this.selectedCategoryFilesData.length && !this.selectedCategoryFilesData.find(fileData => !fileData.selected));
    },
    isAllPagedFilesSelected() {
      return (!!this.pagedFilesData.length && !this.pagedFilesData.find(fileData => !fileData.selected));
    },
    categoryPopupSelectedData() {
      return this.projectCategoriesStateData.find(category => category['@id'] === this.categoryPopupSelected);
    },
    confirmationFilesText() {
      return `Вы действительно хотите ${this.filesAction === 'delete' ? 'удалить' : 'переместить'} ${utils.pluralWord(this.filesActioned.length, 'файл', 'файла', 'файлов')}?`;
    },
    confirmationCategoriesText() {
      let result = '';
      switch (this.categoriesAction) {
        case 'delete':
          result = 'Вы действительно хотите удалить папку?';
          break;
        case 'rename':
          result = `Вы действительно хотите переименовать папку в "${this.categoryNewName}"?`;
          break;
        case 'create':
          result = `Вы действительно хотите создать папку "${this.categoryNewName}"?`;
          break;
        default:
          result = `Вы действительно хотите создать папку"${this.categoryNewName}"?`;
      }
      return result;
    },
    dragText() {
      return this.filesActioned.length === 1 ? this.filesActioned[0].file.name
        : `Выбран${this.getWordEnding(this.selectedFilteredSortedFilesData.length)} ${this.selectedFilesWord} - ${this.selectedFilesStorageSize.toUpperCase()}`;
    },
  },
  methods: {
    changeFileSelection(fileDataId) {
      const foundFileData = this.filesStateDataCopyMarked.find(fileData => fileData.id === fileDataId);

      if (foundFileData) foundFileData.selected = !foundFileData.selected;
    },
    clickFileCategory(categoryId) {
      this.selectedFileCategory = categoryId;
    },
    changeFilters(value, field) {
      this.filters[field] = value;
    },
    changeSorter(value) {
      if (!value) this.sorter = { value, direction: null };
      else {
        const splitter = value.split('_');
        this.sorter = { value: splitter[1], direction: splitter[0] };
      }
    },
    changeOrientationType(type) {
      if (this.device !== 'mobile') this.orientationType = type;
    },
    changePage(page) {
      this.page = page;
    },
    changePageSize(pageSize) {
      this.pageSize = pageSize;
      this.page = 1;
    },
    getWordEnding(length, female) {
      if ((`${length}`)[`${length}`.length - 1] === '1' && (length === 1 || length > 20)) return female ? 'а' : '';
      return female ? 'ы' : 'о';
    },
    toggleSelectedFiles(value, isAll) {
      this.filesStateDataCopyMarked = this.filesStateDataCopyMarked.map(fileData => {
        if (isAll) {
          if (this.selectedCategoryFilesData.find(categoryFileData => categoryFileData.id === fileData.id)) {
            // eslint-disable-next-line no-param-reassign
            fileData.selected = value;
          }
        } else if (this.pagedFilesData.find(pagedFileData => pagedFileData.id === fileData.id)) {
          // eslint-disable-next-line no-param-reassign
          fileData.selected = value;
        }
        return fileData;
      });
    },
    closeMenu(e) {
      if (this.$refs.actions && !this.$refs.actions.contains(e.target)) {
        this.isOpenedMenu = false;
      }
    },
    closeFilesConfirmation() {
      this.filesAction = null;
      this.filesActioned = [];
      this.categoryPopupSelected = null;
      this.confirmFilesPopupOpened = false;
    },
    closeCategoriesConfirmation() {
      this.categoriesAction = null;
      this.categoriesActioned = [];
      this.categoryNewName = null;
      this.confirmCategoriesPopupOpened = false;
    },
    async applyCategoriesConfirmation() {
      this.confirmCategoriesPopupOpened = false;

      const promises = [];
      if (this.categoriesAction === 'create') {
        const maxSort = this.projectCategoriesStateData.sort((cat1, cat2) => cat2.sort - cat1.sort)[1]?.sort ?? 0;

        this.categoriesActioned = [{ name: this.categoryNewName, project: `/api/projects/${this.projectId}`, sort: maxSort + 1 }];
      }
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.categoriesActioned.length; i++) {
        if (this.categoriesAction === 'delete') {
          const promise = projectFileCategoryApi.delete(this.categoriesActioned[i].id);
          promises.push(promise);
        } else if (this.categoriesAction === 'rename') {
          const promise = projectFileCategoryApi.update(this.categoriesActioned[i].id, { name: this.categoryNewName });
          promises.push(promise);
        } else if (this.categoriesAction === 'create') {
          const promise = projectFileCategoryApi.create({ ...this.categoriesActioned[i] });
          promises.push(promise);
        }
      }
      Promise.all(promises).then(result => {
        if (this.categoriesAction === 'delete') {
          const text = this.categoriesActioned.length === 1 ? 'Папка удалена'
            : `${utils.pluralWord(this.categoriesActioned.length, 'папка', 'папки', 'папок')} успешно удален${this.getWordEnding(result.length, true)}`;

          this.$push({ text });
          if (this.categoriesActioned.find(category => category['@id'] === this.selectedFileCategory)) this.selectedFileCategory = null;
        } else if (this.categoriesAction === 'rename' && result.filter(file => file.name === this.categoryNewName).length === this.categoriesActioned.length) {
          const text = this.categoriesActioned.length === 1 ? 'Папка переименована'
            : `${utils.pluralWord(this.categoriesActioned.length, 'папка', 'папки', 'папок')} успешно переименован${this.getWordEnding(result.length, true)}`;

          this.$push({ text });
        } else if (this.categoriesAction === 'create') {
          const text = this.categoriesActioned.length === 1 ? 'Папка создана'
            : `${utils.pluralWord(this.categoriesActioned.length, 'папка', 'папки', 'папок')} успешно создан${this.getWordEnding(result.length, true)}`;

          this.$push({ text });
        }
        this.categoriesAction = null;
        this.categoriesActioned = [];
        this.categoryNewName = null;
        this.$store.dispatch('projectFile/fetchCategories', { projectId: this.projectId, force: true });
        this.$store.dispatch('projectFile/fetchFiles', { projectId: this.projectId, force: true });
      });
    },
    async applyFilesConfirmation() {
      this.confirmFilesPopupOpened = false;

      const promises = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.filesActioned.length; i++) {
        if (this.filesAction === 'delete') {
          const promise = projectFileApi.delete(this.filesActioned[i].id);
          promises.push(promise);
        } else {
          const promise = projectFileApi.update(this.filesActioned[i].id, { category: this.categoryPopupSelected });
          promises.push(promise);
        }
      }
      Promise.all(promises).then(result => {
        if (this.filesAction === 'delete') {
          const text = `${utils.pluralWord(this.filesActioned.length, 'файл', 'файла', 'файлов')} успешно удален${this.getWordEnding(result.length)}`;

          this.$push({ text });
        } else if (result.filter(file => file.category === this.categoryPopupSelected).length === this.filesActioned.length) {
          const text = `${utils.pluralWord(this.filesActioned.length, 'файл', 'файла', 'файлов')}
              успешно перемещен${this.getWordEnding(result.length)} в папку ${this.categoryPopupSelectedData.name}`;

          this.$push({ text });
        }
        this.filesAction = null;
        this.filesActioned = [];
        this.categoryPopupSelected = null;
        // this.clearFilters();
        this.$store.dispatch('projectFile/fetchCategories', { projectId: this.projectId, force: true });
        this.$store.dispatch('projectFile/fetchFiles', { projectId: this.projectId, force: true });
      });
    },
    clearFilters() {
      this.filters.fileName = '';
      this.filters.extensions = [];
      this.filters.members = [];
      this.filters.selected = null;
      this.selectedFileCategory = null;
    },
    applyFilters() {
      this.closeFilesConfirmation();
      this.clearFilters();
      this.filters.selected = true;
    },
    closeCategoryInput() {
      this.categoryInputPopupOpened = false;
      this.categoriesAction = null;
      this.categoriesActioned = [];
      this.categoryNewName = null;
    },
    closeCategorySelection() {
      this.categorySelectPopupOpened = false;
      this.filesAction = null;
      this.filesActioned = [];
      this.categoryPopupSelected = null;
    },
    clickSelectedMenu(action) {
      this.filesActioned = [...this.selectedFilteredSortedFilesData];
      this.filesAction = action;
      if (action === 'move') {
        this.categorySelectPopupOpened = true;
      } else this.checkFileAction();
    },
    clickFileMenu(action, fileData) {
      this.filesActioned = [fileData];
      this.filesAction = action;
      if (action === 'move') {
        this.categorySelectPopupOpened = true;
      } else this.checkFileAction();
    },
    clickCategoryMenu(action, categoryData) {
      this.categoriesActioned = [categoryData];
      this.categoriesAction = action;
      if (action === 'rename') {
        this.categoryInputPopupOpened = true;
        this.categoryNewName = categoryData.name;
      } else this.checkCategoriesAction();
    },
    setCreatingAction() {
      this.categoriesActioned = [];
      this.categoriesAction = 'create';
      this.categoryInputPopupOpened = true;
      this.categoryNewName = null;
    },
    async checkCategoriesAction() {
      if (this.categoriesAction === 'delete') {
        if (await this.checkDeleteAllowed()) {
          this.categoryInputPopupOpened = false;
          this.confirmCategoriesPopupOpened = true;
        }
      } else {
        this.categoryInputPopupOpened = false;
        this.applyCategoriesConfirmation();
      }
    },
    async checkDeleteAllowed() {
      const res = await projectFileCategoryApi.fetchList({ project: this.projectId }, true);
      if (res.total > 1) return true;

      const { name } = this.categoriesActioned[0];
      this.$alert(`Невозможно удалить папку "[${name}]". В проекте должна быть хотя бы одна папка, кроме папки "[Все]". Чтобы удалить папку "[${name}]", сначала создайте другую папку.`);
      return false;
    },
    async checkFileAction() {
      this.categorySelectPopupOpened = false;
      if (this.filesAction === 'download') {
        if (this.filesActioned.length === 1) {
          utils.downloadFile(this.filesActioned[0]);
        } else {
          const files = this.filesActioned.map((f) => `files[]=${f.file.id}`).join('&');
          window.open(`/api/download?${files}`);
        }
        this.filesAction = null;
        this.filesActioned = [];
      } else {
        this.confirmFilesPopupOpened = true;
      }
    },
    checkResize() {
      if (window.innerWidth > 1200) this.device = 'desktop';
      else if (window.innerWidth < 768) this.device = 'mobile';
      else this.device = 'tablet';
    },
    fileDragStart(e, fileData) {
      this.filesAction = 'drag';
      this.filesActioned = fileData.selected ? [...this.selectedFilteredSortedFilesData] : [fileData];
      this.dragActive = true;
      /* без nextTick будет резкое появление плашки чуть правее, чем нужно. Видимо потому, что в этот
      момент computed ещё считается, а this.$refs['drag-bar'].offsetWidth равен нулю. Нужно подождать,
      пока плашка нарисуется, и потом считать координату по её сформировавшейся длине */
      this.$nextTick(() => {
        this.$refs['drag-bar'].style.left = `${e.x - this.$refs['drag-bar'].offsetWidth}px`;
        this.$refs['drag-bar'].style.top = `${e.y - 48}px`;
      });
    },
    fileDrag(e) {
      this.$refs['drag-bar'].style.left = `${e.x - this.$refs['drag-bar'].offsetWidth}px`;
      this.$refs['drag-bar'].style.top = `${e.y - 48}px`;
    },
    fileDrop(categoryId) {
      this.categoryPopupSelected = categoryId;
      this.checkFileAction();
    },
    checkEnter() {
      if (this.categoryNewName) this.checkCategoriesAction();
    },
  },
};
</script>

<template lang="pug">
  CommonSection(title="Файлы проекта" :back="{ title: project && project.name, name: 'project-stages', params: { projectId } }")
    template(#button)
      ProjectFileUploader(:project="projectId" :publish="true" buttonText="Добавить" buttonIcon="plus" noUploadedText)
    BasePopup.b-category-popup(v-if="categoryInputPopupOpened" @close="closeCategoryInput") {{categoriesAction === 'rename' ? 'Переименовать' : 'Создать'}} папку
      BaseInput.__input(v-model="categoryNewName" :class="device" @keyup.enter="checkEnter")
      Btn(:disabled="!categoryNewName" @click="checkCategoriesAction") Сохранить
    BasePopup.b-confirm-popup(v-if="confirmCategoriesPopupOpened" @close="closeCategoriesConfirmation")
      .__wrapper
        .__confirmation-text {{ confirmationCategoriesText }}
        .__buttons-container
          Btn(@click="applyCategoriesConfirmation") {{ categoriesAction === 'delete' ? 'Удалить' : categoriesAction === 'rename' ? 'Переименовать' : 'Создать'}}
          Btn(@click="closeCategoriesConfirmation") Отменить
    BasePopup.b-category-popup(v-if="categorySelectPopupOpened" @close="closeCategorySelection") Выбор папки
      BaseSelect.__select(v-model="categoryPopupSelected" :options="categoriesPopup" labelOuter notSelectedLabel="Папка" :class="device")
      Btn(:disabled="!categoryPopupSelected" @click="checkFileAction") Выбрать
    BasePopup.b-confirm-popup(v-if="confirmFilesPopupOpened" @close="closeFilesConfirmation")
      .__wrapper
        .__confirmation-text {{ confirmationFilesText }}
        .__warning-text(v-if="filesAction === 'delete'") Файлы не подлежат восстановлению
        .__buttons-container
          Btn(@click="applyFilesConfirmation") {{ filesAction === 'delete' ? 'Удалить' : 'Переместить' }}
          Btn(@click="closeFilesConfirmation") Отменить
        .__applier(v-if="filesActioned.length > 1" @click="applyFilters" :class="device") Посмотреть выбранные {{ device !== 'mobile' ? 'файлы' : '' }}
    .drag-bar(v-show="dragActive" ref="drag-bar")
      Icon.icon(name="file")
      .text {{ dragText }}
    .b-files-page
      ProjectFileFiltersContainer.__filters-container(:filters="filters" @changeFilters="changeFilters"
        :filesData="projectFilesStateData"
        @changeSorter="changeSorter" @changeOrientationType="changeOrientationType" @clearFilters="clearFilters")
      .__data-container
        ProjectFileCategoriesContainer.__categories-container(:filesCategoriesData="projectCategoriesStateData"
          :filesData="filteredSortedFilesData" @clickFileCategory="clickFileCategory" :selectedCategory="selectedFileCategory"
          @fileDrop="fileDrop" @clickMenu="clickCategoryMenu" @createCategory="setCreatingAction" :isEditable="categoriesAreEditable")
        .__files-table(v-if="selectedCategoryFilesData.length" :class="'orientation-' + orientationType")
          ProjectFilesContainer(:filesData="pagedFilesData" :orientationType="orientationType"
            @changeFileSelection="changeFileSelection" @clickMenu="clickFileMenu" @fileDrag="fileDrag"
            @fileDragStart="fileDragStart")
          Pagination.__pagination-container(:dataLength="selectedCategoryFilesData.length"
            @changePage="changePage" @changePageSize="changePageSize"
            :externalPageSize="pageSize" :externalActivePage="page" ref="pagination")
        EmptyList.__empty(v-else) Нет файлов
    Teleport(to="#footer-bar")
      ProjectFilesFooterBar(
        :filesData="filteredSortedFilesData"
        :selectedFilesData="selectedFilteredSortedFilesData"
        :pagedFilesData="pagedFilesData"
        :selectedCategoryFilesData="selectedCategoryFilesData"
        @toggleSelectedFiles="toggleSelectedFiles($event.value, $event.isAll)"
        @action="clickSelectedMenu")
</template>

<style lang="scss">
  .files-page__files-table.orientation-square {
    .project-file-frame {
      max-width: calc((100% - 3*15px)/4); //Ширина контейнера 100%, минус три gap

      @media (max-width: 1670px) {
        max-width: calc((100% - 2*15px)/3);
      }

      @media (max-width: 1168px) {
        max-width: calc((100% - 15px)/2);
      }

      @media (max-width: 680px) {
        max-width: 100%;
      }
    }
  }
</style>

<style scoped lang="scss">
  .files-page {
    position: relative;
    padding-bottom: 60px;

    &__files-table {
      width: calc(100% - 350px);
      box-sizing: border-box;
      background: #FFFFFF;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
      border-radius: 8px;
      padding: 32px;
      flex-shrink: 0;

      @media (max-width: 1400px) {
        width: 100%;
      }

      @media (max-width: 1024px) {
        align-self: center;
        padding: 20px;
      }

      &.orientation-square {
        flex-wrap: wrap;

        @media (max-width: 768px) {
          width: 100%;
          align-self: flex-start;
        }
      }
    }

    &__data-container {
      display: flex;
      align-items: flex-start;
      position: relative;

      @media (max-width: 1400px) {
        flex-direction: column;
      }
    }

    &__filters-container {
      margin-bottom: 38px;

      @media (max-width: 768px) {
        width: 100%;
        gap: 15px 0;
        flex-wrap: wrap;
      }
    }

    &__categories-container {
      width: 312px;
      margin-right: 38px;
      background: #FFFFFF;
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
      border-radius: 8px;
      padding: 32px 18px 32px 32px;
      box-sizing: border-box;

      @media (max-width: 1400px) {
        white-space: nowrap;
        background: transparent;
        box-shadow: none;
        padding: 0 0 18px 0;
        margin-bottom: 18px;
        width: 100%;
      }
    }

    &__pagination-container {
      margin-top: 35px;
    }

    &__empty {
      width: 100%;
    }
  }

  .category-popup {
    &__select {
      width: 300px;
      margin-bottom: 20px;
      margin-top: 15px;

      &.mobile {
        width: calc(100vw - 100px);
        max-width: 300px;
      }
    }

    &__input {
      width: 300px;
      margin-bottom: 20px;
      margin-top: 15px;

      &.mobile {
        width: calc(100vw - 100px);
        max-width: 300px;
      }
    }
  }

  .confirm-popup {

    &__wrapper {
      box-sizing: border-box;
      width: 600px;
      padding: 0 150px;
      display: flex;
      flex-direction: column;
      align-items: center;

      @media (max-width: 768px) {
        width: calc(100vw - 100px);
        padding: 0 20px;
      }
    }

    &__buttons-container {
      margin-top: 10px;
      display: flex;
      justify-content: flex-start;
      gap: 10px;
    }

    &__confirmation-text {
      text-align: center;
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 24px;
      color: #4A535C;
    }

    &__warning-text {
      text-align: center;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #B42727;
      margin: 10px 0;
    }

    &__applier {
      cursor: pointer;
      text-align: center;
      font-style: normal;
      font-weight: 400;
      font-size: 12px;
      line-height: 20px;
      margin-top: 30px;
      color: #1E3147;
      border-bottom: 1px dashed #1E3147;

      &.mobile {
        width: calc(100vw - 100px);
        max-width: 300px;
      }
    }
  }

  .drag-bar {
    display: flex;
    align-items: center;
    background: #588CF4;
    border-radius: 4px;
    padding: 12px 20px;
    height: 48px;
    box-sizing: border-box;
    position: fixed;
    width: max-content;
    z-index: 9999;

    .icon {
      fill: white;
      svg {
        width: 11px;
        height: 13px;
        margin-right: 10px;
      }
      path {
        fill: white;
      }
    }

    .text {
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-white);
      margin-left: 10px;
    }
  }

</style>
